import React, { useMemo, useState } from 'react';
import { UserBaseline } from '../types';
import { api } from '../services/api';

interface AiColumnMapping {
  detected_columns: {
    property_name: string | null;
    property_url: string | null;
    phone_number: string | null;
    address: string | null;
    city: string | null;
    country: string | null;
  };
  halal_columns: {
    halal_all: string | null;
    halal_some: string | null;
    halal_on_request: string | null;
    halal_nearby: string | null;
  };
  alcohol_columns: {
    alcohol_property: string | null;
    alcohol_restaurant: string | null;
    alcohol_room: string | null;
  };
  leisure_columns: {
    modest_swimwear: string | null;
    women_only: string | null;
    private_hire: string | null;
  };
  confidence: number;
  analysis_summary: string;
  all_headers: string[];
  sample_data: Record<string, string>[];
  total_rows: number;
  csv_content: string;
}

interface CsvMap {
  phone?: string;
  property?: string;
  property_url?: string;
  halal_all?: string;
  halal_some?: string;
  halal_on_request?: string;
  halal_nearby?: string;
  alcohol_property?: string;
  alcohol_restaurant?: string;
  alcohol_room?: string;
  modest_swimwear?: string;
  women_only?: string;
  private_hire?: string;
}

const truthy = (v: string | undefined) => {
  if (!v) return false;
  const s = String(v).trim().toLowerCase();
  return ['1','true','yes','y'].includes(s);
}

const BulkRunPanel: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [batchName, setBatchName] = useState('');
  const [defaultBaseline, setDefaultBaseline] = useState<UserBaseline>({});
  const [rows, setRows] = useState<Array<{ phoneNumber: string; propertyName?: string; propertyUrl?: string; userBaseline: UserBaseline }>>([
    { phoneNumber: '', propertyName: '', propertyUrl: '', userBaseline: {} as UserBaseline }
  ]);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [csvMap, setCsvMap] = useState<CsvMap>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  // Pagination state for bulk entries
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 25;

  // AI Upload state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [aiFile, setAiFile] = useState<File | null>(null);
  const [aiParsing, setAiParsing] = useState(false);
  const [aiMapping, setAiMapping] = useState<AiColumnMapping | null>(null);
  const [aiConfirmedMap, setAiConfirmedMap] = useState<CsvMap>({});

  const onCsvUpload = async (file: File) => {
    const text = await file.text();
    const lines = text.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length === 0) return;
    const headers = lines[0].split(',').map(h => h.trim());
    const rows: Record<string, string>[] = [];
    for (let i = 1; i < lines.length; i++) {
      const cols = lines[i].split(',');
      const row: Record<string, string> = {};
      headers.forEach((h, idx) => {
        row[h] = (cols[idx] ?? '').trim();
      });
      rows.push(row);
    }
    setCsvHeaders(headers);
    setCsvRows(rows);
  };

  const onAiCsvUpload = async (file: File) => {
    setAiFile(file);
    setAiParsing(true);
    setMessage(null);

    try {
      const text = await file.text();
      const result = await api.parseCsv(text, file.name);
      setAiMapping(result);

      // Auto-populate the mapping based on AI detection
      const autoMap: CsvMap = {};
      if (result.detected_columns.phone_number) autoMap.phone = result.detected_columns.phone_number;
      if (result.detected_columns.property_name) autoMap.property = result.detected_columns.property_name;
      if (result.detected_columns.property_url) autoMap.property_url = result.detected_columns.property_url;

      if (result.halal_columns.halal_all) autoMap.halal_all = result.halal_columns.halal_all;
      if (result.halal_columns.halal_some) autoMap.halal_some = result.halal_columns.halal_some;
      if (result.halal_columns.halal_on_request) autoMap.halal_on_request = result.halal_columns.halal_on_request;
      if (result.halal_columns.halal_nearby) autoMap.halal_nearby = result.halal_columns.halal_nearby;

      if (result.alcohol_columns.alcohol_property) autoMap.alcohol_property = result.alcohol_columns.alcohol_property;
      if (result.alcohol_columns.alcohol_restaurant) autoMap.alcohol_restaurant = result.alcohol_columns.alcohol_restaurant;
      if (result.alcohol_columns.alcohol_room) autoMap.alcohol_room = result.alcohol_columns.alcohol_room;

      if (result.leisure_columns.modest_swimwear) autoMap.modest_swimwear = result.leisure_columns.modest_swimwear;
      if (result.leisure_columns.women_only) autoMap.women_only = result.leisure_columns.women_only;
      if (result.leisure_columns.private_hire) autoMap.private_hire = result.leisure_columns.private_hire;

      setAiConfirmedMap(autoMap);

      setMessage(`AI analysis complete! Confidence: ${result.confidence}%. ${result.analysis_summary}`);
    } catch (error: any) {
      setMessage(`AI parsing failed: ${error.message}`);
      setAiMapping(null);
    } finally {
      setAiParsing(false);
    }
  };

  // Pagination calculations
  const totalRows = rows.length;
  const totalPages = Math.ceil(totalRows / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedRows = rows.slice(startIndex, endIndex);

  // Pagination handlers
  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const goToPrevious = () => goToPage(currentPage - 1);
  const goToNext = () => goToPage(currentPage + 1);

  const addRow = () => {
    const newRows = [...rows, { phoneNumber: '', propertyName: '', propertyUrl: '', userBaseline: { ...defaultBaseline } }];
    setRows(newRows);

    // If adding to a new page, go to that page
    const newTotalPages = Math.ceil(newRows.length / itemsPerPage);
    if (newTotalPages > totalPages) {
      setCurrentPage(newTotalPages);
    }
  };

  const removeRow = (globalIdx: number) => {
    const newRows = rows.filter((_, i) => i !== globalIdx);
    setRows(newRows);

    // Adjust current page if necessary
    const newTotalPages = Math.ceil(newRows.length / itemsPerPage);
    if (currentPage > newTotalPages && newTotalPages > 0) {
      setCurrentPage(newTotalPages);
    }
  };

  const updateRow = (globalIdx: number, patch: Partial<{ phoneNumber: string; propertyName?: string; propertyUrl?: string; userBaseline: UserBaseline }>) => {
    setRows(prev => prev.map((r, i) => i === globalIdx ? { ...r, ...patch, userBaseline: patch.userBaseline ? { ...patch.userBaseline } : r.userBaseline } : r));
  };

  const buildEntriesFromCsv = () => {
    return csvRows.map(row => {
      const phoneNumber = csvMap.phone ? row[csvMap.phone] : '';
      const propertyName = csvMap.property ? row[csvMap.property] : undefined;
      const propertyUrl = csvMap.property_url ? row[csvMap.property_url] : undefined;
      const userBaseline: UserBaseline = {
        halalOptions: {
          all: truthy(csvMap.halal_all ? row[csvMap.halal_all] : undefined) ? 'yes' : 'no',
          some: truthy(csvMap.halal_some ? row[csvMap.halal_some] : undefined) ? 'yes' : 'no',
          onRequest: truthy(csvMap.halal_on_request ? row[csvMap.halal_on_request] : undefined) ? 'yes' : 'no',
          nearby: truthy(csvMap.halal_nearby ? row[csvMap.halal_nearby] : undefined) ? 'yes' : 'no',
        },
        alcoholOptions: {
          property: truthy(csvMap.alcohol_property ? row[csvMap.alcohol_property] : undefined) ? 'yes' : 'no',
          restaurant: truthy(csvMap.alcohol_restaurant ? row[csvMap.alcohol_restaurant] : undefined) ? 'yes' : 'no',
          room: truthy(csvMap.alcohol_room ? row[csvMap.alcohol_room] : undefined) ? 'yes' : 'no',
        },
        leisureFacilities: {
          modestSwimwear: truthy(csvMap.modest_swimwear ? row[csvMap.modest_swimwear] : undefined) ? 'yes' : 'no',
          womenOnlyFacilities: truthy(csvMap.women_only ? row[csvMap.women_only] : undefined) ? 'yes' : 'no',
          privateHire: truthy(csvMap.private_hire ? row[csvMap.private_hire] : undefined) ? 'yes' : 'no',
        }
      };
      return { phoneNumber, propertyName, propertyUrl, userBaseline };
    });
  };

  const buildEntriesFromAiCsv = () => {
    if (!aiMapping) return [];

    // Parse all rows from the original CSV content
    const lines = aiMapping.csv_content.split(/\r?\n/).filter(l => l.trim().length > 0);
    if (lines.length <= 1) return []; // No data rows

    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));
    const allRows = lines.slice(1); // Get ALL data rows

    return allRows.map((row, index) => {
      const cols = row.split(',').map(c => c.trim().replace(/"/g, ''));
      const rowObj: Record<string, string> = {};
      headers.forEach((header, idx) => {
        rowObj[header] = cols[idx] || '';
      });

      const phoneNumber = aiConfirmedMap.phone ? rowObj[aiConfirmedMap.phone] : '';
      const propertyName = aiConfirmedMap.property ? rowObj[aiConfirmedMap.property] : undefined;
      const propertyUrl = aiConfirmedMap.property_url ? rowObj[aiConfirmedMap.property_url] : undefined;

      const userBaseline: UserBaseline = {
        halalOptions: {
          all: truthy(aiConfirmedMap.halal_all ? rowObj[aiConfirmedMap.halal_all] : undefined) ? 'yes' : 'no',
          some: truthy(aiConfirmedMap.halal_some ? rowObj[aiConfirmedMap.halal_some] : undefined) ? 'yes' : 'no',
          onRequest: truthy(aiConfirmedMap.halal_on_request ? rowObj[aiConfirmedMap.halal_on_request] : undefined) ? 'yes' : 'no',
          nearby: truthy(aiConfirmedMap.halal_nearby ? rowObj[aiConfirmedMap.halal_nearby] : undefined) ? 'yes' : 'no',
        },
        alcoholOptions: {
          property: truthy(aiConfirmedMap.alcohol_property ? rowObj[aiConfirmedMap.alcohol_property] : undefined) ? 'yes' : 'no',
          restaurant: truthy(aiConfirmedMap.alcohol_restaurant ? rowObj[aiConfirmedMap.alcohol_restaurant] : undefined) ? 'yes' : 'no',
          room: truthy(aiConfirmedMap.alcohol_room ? rowObj[aiConfirmedMap.alcohol_room] : undefined) ? 'yes' : 'no',
        },
        leisureFacilities: {
          modestSwimwear: truthy(aiConfirmedMap.modest_swimwear ? rowObj[aiConfirmedMap.modest_swimwear] : undefined) ? 'yes' : 'no',
          womenOnlyFacilities: truthy(aiConfirmedMap.women_only ? rowObj[aiConfirmedMap.women_only] : undefined) ? 'yes' : 'no',
          privateHire: truthy(aiConfirmedMap.private_hire ? rowObj[aiConfirmedMap.private_hire] : undefined) ? 'yes' : 'no',
        }
      };
      return { phoneNumber, propertyName, propertyUrl, userBaseline };
    });
  };

  const csvPreviewCount = useMemo(() => csvRows.length, [csvRows]);

  const downloadExampleCsv = () => {
    const headers = [
      'phone','property','property_url','halal_all','halal_some','halal_on_request','halal_nearby',
      'alcohol_property','alcohol_restaurant','alcohol_room','modest_swimwear','women_only','private_hire'
    ];
    const exampleRows = [
      ['+447395443158','Seaview Hotel','https://example.com/seaview','1','1','0','1','1','0','0','1','0','0'],
      ['+442079460123','City Suites','https://example.com/city-suites','0','1','1','0','0','1','0','0','1','0'],
      ['+97145551234','Palm Resort','https://example.com/palm-resort','1','0','0','1','1','1','1','1','1','1']
    ];
    const csv = [headers.join(',')].concat(exampleRows.map(r => r.join(','))).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk-example.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleSubmit = async () => {
    try {
      setIsSubmitting(true);
      setMessage(null);
      const entries: Array<{ phoneNumber: string; userBaseline?: UserBaseline; propertyName?: string; propertyUrl?: string }> = rows.filter(r => r.phoneNumber);
      if (entries.length === 0) {
        setMessage('No valid entries');
        return;
      }
      const res = await api.bulkInitiate(entries, { name: batchName });
      setMessage(`Batch ${res.batchId} started. ${res.results.filter(r => r.callId).length} calls created, ${res.results.filter(r => r.error).length} errors.`);
      setCsvHeaders([]);
      setCsvRows([]);
      setCsvMap({});
      setDefaultBaseline({});
      setBatchName('');
      setRows([{ phoneNumber: '', propertyName: '', propertyUrl: '', userBaseline: {} as UserBaseline }]);
      setCurrentPage(1); // Reset to first page
    } catch (e: any) {
      setMessage(`Bulk start failed: ${e?.message || 'unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bulk-panel">
      <div className="bulk-header">
        <h2>Bulk Run</h2>
        <button className="toggle" onClick={() => setOpen(!open)}>
          {open ? 'Collapse Bulk Panel' : 'Expand Bulk Panel'}
        </button>
      </div>
      {open && (
        <div className="bulk-body">
          <div className="row">
            <label>Batch Name</label>
            <input value={batchName} onChange={e => setBatchName(e.target.value)} placeholder="e.g., October sweep" />
          </div>

          <div className="section">
            <h3>Manual Entries</h3>
            <div className="rows">
              <div className="row head">
                <span className="col phone">Phone</span>
                <span className="col prop">Property</span>
                <span className="col prop">URL</span>
                <div className="col ticks compact header halal-header">
                  <span className="header-label">Halal</span>
                  <div className="header-spacers">
                    <span className="spacer">All</span>
                    <span className="spacer">Some</span>
                    <span className="spacer">Req</span>
                    <span className="spacer">Near</span>
                  </div>
                </div>
                <div className="col ticks compact header alcohol-header">
                  <span className="header-label">Alc</span>
                  <div className="header-spacers">
                    <span className="spacer">Prop</span>
                    <span className="spacer">Rest</span>
                    <span className="spacer">Room</span>
                  </div>
                </div>
                <div className="col ticks compact header leisure-header">
                  <span className="header-label">Leisure</span>
                  <div className="header-spacers">
                    <span className="spacer">Mod</span>
                    <span className="spacer">Wmn</span>
                    <span className="spacer">Priv</span>
                  </div>
                </div>
                <span className="col act"></span>
              </div>
              {paginatedRows.map((r, pageIdx) => {
                const globalIdx = startIndex + pageIdx;
                return (
                  <div className="row compact" key={globalIdx}>
                    <input className="col phone compact" value={r.phoneNumber} onChange={e => updateRow(globalIdx, { phoneNumber: e.target.value })} placeholder="+44..." />
                    <input className="col prop compact" value={r.propertyName || ''} onChange={e => updateRow(globalIdx, { propertyName: e.target.value })} placeholder="Property" />
                    <input className="col prop compact" value={r.propertyUrl || ''} onChange={e => updateRow(globalIdx, { propertyUrl: e.target.value })} placeholder="URL" />
                    <div className="col ticks compact">
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.halalOptions?.all === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, halalOptions: { ...r.userBaseline?.halalOptions, all: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />All</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.halalOptions?.some === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, halalOptions: { ...r.userBaseline?.halalOptions, some: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Some</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.halalOptions?.onRequest === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, halalOptions: { ...r.userBaseline?.halalOptions, onRequest: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Req</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.halalOptions?.nearby === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, halalOptions: { ...r.userBaseline?.halalOptions, nearby: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Near</label>
                    </div>
                    <div className="col ticks compact">
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.alcoholOptions?.property === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, alcoholOptions: { ...r.userBaseline?.alcoholOptions, property: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Prop</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.alcoholOptions?.restaurant === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, alcoholOptions: { ...r.userBaseline?.alcoholOptions, restaurant: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Rest</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.alcoholOptions?.room === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, alcoholOptions: { ...r.userBaseline?.alcoholOptions, room: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Room</label>
                    </div>
                    <div className="col ticks compact">
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.leisureFacilities?.modestSwimwear === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, leisureFacilities: { ...r.userBaseline?.leisureFacilities, modestSwimwear: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Mod</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.leisureFacilities?.womenOnlyFacilities === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, leisureFacilities: { ...r.userBaseline?.leisureFacilities, womenOnlyFacilities: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Wmn</label>
                      <label className="mini"><input type="checkbox" checked={r.userBaseline?.leisureFacilities?.privateHire === 'yes'} onChange={e => updateRow(globalIdx, { userBaseline: { ...r.userBaseline, leisureFacilities: { ...r.userBaseline?.leisureFacilities, privateHire: e.target.checked ? 'yes' : 'no' } } as UserBaseline })} />Priv</label>
                    </div>
                    <div className="col act">
                      <button className="del compact" onClick={() => removeRow(globalIdx)}>×</button>
                    </div>
                  </div>
                );
              })}
              <div className="row add">
                <button className="add-btn" onClick={addRow}>+ Add Row</button>
              </div>
            </div>

            {/* Bulk Pagination Controls */}
            {totalPages > 1 && (
              <div className="bulk-pagination">
                <button
                  className="pagination-btn"
                  onClick={goToPrevious}
                  disabled={currentPage === 1}
                  title="Previous page"
                >
                  ← Previous
                </button>

                <div className="pagination-info">
                  <span className="page-indicator">
                    Page {currentPage} of {totalPages}
                  </span>
                  <span className="item-count">
                    {startIndex + 1}-{Math.min(endIndex, totalRows)} of {totalRows} entries
                  </span>
                </div>

                <button
                  className="pagination-btn"
                  onClick={goToNext}
                  disabled={currentPage === totalPages}
                  title="Next page"
                >
                  Next →
                </button>
              </div>
            )}

            <div className="baseline compact">
              <h4>Default Baseline</h4>
              <div className="ticks compact">
                <div className="baseline-row compact">
                  <span className="title compact">Halal:</span>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.halalOptions?.all === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, halalOptions: { ...defaultBaseline.halalOptions, all: e.target.checked ? 'yes' : 'no' } })} />All</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.halalOptions?.some === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, halalOptions: { ...defaultBaseline.halalOptions, some: e.target.checked ? 'yes' : 'no' } })} />Some</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.halalOptions?.onRequest === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, halalOptions: { ...defaultBaseline.halalOptions, onRequest: e.target.checked ? 'yes' : 'no' } })} />Req</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.halalOptions?.nearby === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, halalOptions: { ...defaultBaseline.halalOptions, nearby: e.target.checked ? 'yes' : 'no' } })} />Near</label>
                </div>
                <div className="baseline-row compact">
                  <span className="title compact">Alc:</span>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.alcoholOptions?.property === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, alcoholOptions: { ...defaultBaseline.alcoholOptions, property: e.target.checked ? 'yes' : 'no' } })} />Prop</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.alcoholOptions?.restaurant === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, alcoholOptions: { ...defaultBaseline.alcoholOptions, restaurant: e.target.checked ? 'yes' : 'no' } })} />Rest</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.alcoholOptions?.room === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, alcoholOptions: { ...defaultBaseline.alcoholOptions, room: e.target.checked ? 'yes' : 'no' } })} />Room</label>
                </div>
                <div className="baseline-row compact">
                  <span className="title compact">Leisure:</span>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.leisureFacilities?.modestSwimwear === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, leisureFacilities: { ...defaultBaseline.leisureFacilities, modestSwimwear: e.target.checked ? 'yes' : 'no' } })} />Mod</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.leisureFacilities?.womenOnlyFacilities === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, leisureFacilities: { ...defaultBaseline.leisureFacilities, womenOnlyFacilities: e.target.checked ? 'yes' : 'no' } })} />Wmn</label>
                  <label className="mini"><input type="checkbox" checked={defaultBaseline.leisureFacilities?.privateHire === 'yes'} onChange={(e) => setDefaultBaseline({ ...defaultBaseline, leisureFacilities: { ...defaultBaseline.leisureFacilities, privateHire: e.target.checked ? 'yes' : 'no' } })} />Priv</label>
                </div>
              </div>
            </div>
          </div>

          <div className="section">
            <h3>CSV Upload</h3>
            <div className="csv-info">
              <p>Download the example CSV to ensure your format is correct, then upload it below.</p>
              <button className="example-btn" onClick={downloadExampleCsv}>Download example CSV</button>
            </div>
            <input type="file" accept=".csv" onChange={(e) => e.target.files && onCsvUpload(e.target.files[0])} />
            {csvHeaders.length > 0 && (
              <div className="mapping">
                <h4>Header Mapping ({csvPreviewCount} rows)</h4>
                {[
                  { key: 'phone', label: 'Phone Number' },
                  { key: 'property', label: 'Property Name' },
                  { key: 'property_url', label: 'Property URL' },
                  { key: 'halal_all', label: 'Halal All' },
                  { key: 'halal_some', label: 'Halal Some' },
                  { key: 'halal_on_request', label: 'Halal On Request' },
                  { key: 'halal_nearby', label: 'Halal Nearby' },
                  { key: 'alcohol_property', label: 'Alcohol-free Property' },
                  { key: 'alcohol_restaurant', label: 'Alcohol-free Restaurant' },
                  { key: 'alcohol_room', label: 'Alcohol-free Room' },
                  { key: 'modest_swimwear', label: 'Modest Swimwear' },
                  { key: 'women_only', label: 'Women-only Facilities' },
                  { key: 'private_hire', label: 'Private Hire' },
                ].map(m => (
                  <div className="map-row" key={m.key}>
                    <label>{m.label}</label>
                    <select value={(csvMap as any)[m.key] || ''} onChange={(e) => setCsvMap({ ...csvMap, [m.key]: e.target.value })}>
                      <option value="">—</option>
                      {csvHeaders.map(h => (
                        <option key={h} value={h}>{h}</option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            )}
            {csvHeaders.length > 0 && (
              <div className="import-actions">
                <button className="import" onClick={() => setRows(buildEntriesFromCsv())}>Import mapped → Rows</button>
              </div>
            )}
          </div>

          <div className="section">
            <h3>AI Upload (Beta)</h3>
            <div className="csv-info">
              <p>Upload any CSV file and AI will automatically detect columns containing property information, phone numbers, and halal/alcohol policies.</p>
            </div>
            <input
              type="file"
              accept=".csv"
              onChange={(e) => e.target.files && onAiCsvUpload(e.target.files[0])}
              disabled={aiParsing}
            />
            {aiParsing && (
              <div className="ai-status">
                <div className="loading">🤖 AI is analyzing your CSV file...</div>
              </div>
            )}
            {aiMapping && (
              <div className="ai-results">
                <h4>AI Analysis Results (Confidence: {aiMapping.confidence}%) - {aiMapping.total_rows} rows detected</h4>
                <p className="analysis-summary">{aiMapping.analysis_summary}</p>

                <div className="ai-mapping">
                  <h5>Detected Columns:</h5>
                  <div className="detected-columns">
                    {[
                      { key: 'phone', label: 'Phone Number', detected: aiMapping.detected_columns.phone_number },
                      { key: 'property', label: 'Property Name', detected: aiMapping.detected_columns.property_name },
                      { key: 'property_url', label: 'Property URL', detected: aiMapping.detected_columns.property_url },
                    ].map(col => (
                      <div className="map-row" key={col.key}>
                        <label>{col.label}</label>
                        <select
                          value={aiConfirmedMap[col.key as keyof CsvMap] || ''}
                          onChange={(e) => setAiConfirmedMap({ ...aiConfirmedMap, [col.key]: e.target.value })}
                        >
                          <option value="">{col.detected ? `🤖 ${col.detected}` : '—'}</option>
                          {aiMapping.all_headers.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <h5>Halal Options:</h5>
                  <div className="halal-columns">
                    {[
                      { key: 'halal_all', label: 'Halal All', detected: aiMapping.halal_columns.halal_all },
                      { key: 'halal_some', label: 'Halal Some', detected: aiMapping.halal_columns.halal_some },
                      { key: 'halal_on_request', label: 'Halal On Request', detected: aiMapping.halal_columns.halal_on_request },
                      { key: 'halal_nearby', label: 'Halal Nearby', detected: aiMapping.halal_columns.halal_nearby },
                    ].map(col => (
                      <div className="map-row" key={col.key}>
                        <label>{col.label}</label>
                        <select
                          value={aiConfirmedMap[col.key as keyof CsvMap] || ''}
                          onChange={(e) => setAiConfirmedMap({ ...aiConfirmedMap, [col.key]: e.target.value })}
                        >
                          <option value="">{col.detected ? `🤖 ${col.detected}` : '—'}</option>
                          {aiMapping.all_headers.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <h5>Alcohol Options:</h5>
                  <div className="alcohol-columns">
                    {[
                      { key: 'alcohol_property', label: 'Alcohol-free Property', detected: aiMapping.alcohol_columns.alcohol_property },
                      { key: 'alcohol_restaurant', label: 'Alcohol-free Restaurant', detected: aiMapping.alcohol_columns.alcohol_restaurant },
                      { key: 'alcohol_room', label: 'Alcohol-free Room', detected: aiMapping.alcohol_columns.alcohol_room },
                    ].map(col => (
                      <div className="map-row" key={col.key}>
                        <label>{col.label}</label>
                        <select
                          value={aiConfirmedMap[col.key as keyof CsvMap] || ''}
                          onChange={(e) => setAiConfirmedMap({ ...aiConfirmedMap, [col.key]: e.target.value })}
                        >
                          <option value="">{col.detected ? `🤖 ${col.detected}` : '—'}</option>
                          {aiMapping.all_headers.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>

                  <h5>Leisure Facilities:</h5>
                  <div className="leisure-columns">
                    {[
                      { key: 'modest_swimwear', label: 'Modest Swimwear', detected: aiMapping.leisure_columns.modest_swimwear },
                      { key: 'women_only', label: 'Women-only Facilities', detected: aiMapping.leisure_columns.women_only },
                      { key: 'private_hire', label: 'Private Hire', detected: aiMapping.leisure_columns.private_hire },
                    ].map(col => (
                      <div className="map-row" key={col.key}>
                        <label>{col.label}</label>
                        <select
                          value={aiConfirmedMap[col.key as keyof CsvMap] || ''}
                          onChange={(e) => setAiConfirmedMap({ ...aiConfirmedMap, [col.key]: e.target.value })}
                        >
                          <option value="">{col.detected ? `🤖 ${col.detected}` : '—'}</option>
                          {aiMapping.all_headers.map(h => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="sample-preview">
                  <h5>Sample Data Preview:</h5>
                  <div className="sample-table">
                    <div className="sample-header">
                      {aiMapping.all_headers.slice(0, 5).map(header => (
                        <div key={header} className="sample-cell header">{header}</div>
                      ))}
                    </div>
                    {aiMapping.sample_data.slice(0, 3).map((row, idx) => (
                      <div key={idx} className="sample-row">
                        {aiMapping.all_headers.slice(0, 5).map(header => (
                          <div key={header} className="sample-cell">{row[header] || ''}</div>
                        ))}
                      </div>
                    ))}
                  </div>
                </div>

                <div className="import-actions">
                  <button className="import" onClick={() => setRows(buildEntriesFromAiCsv())}>
                    Import AI-mapped → Rows ({aiMapping.total_rows} entries)
                  </button>
                </div>
              </div>
            )}
          </div>

          <div className="actions">
            <button className="start" onClick={handleSubmit} disabled={isSubmitting}>{isSubmitting ? 'Starting…' : 'Start Bulk Run'}</button>
            {message && <div className="message">{message}</div>}
          </div>
        </div>
      )}

      <style>{`
        .bulk-panel {
          background: var(--hb-bg-secondary);
          padding: 0;
          border-radius: var(--hb-radius-lg);
          box-shadow: var(--hb-shadow);
          border: 1px solid var(--hb-border);
          margin-bottom: 2rem;
          overflow: hidden;
        }

        .bulk-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1.5rem 1.5rem 1rem 1.5rem;
          background: linear-gradient(135deg, var(--hb-bg) 0%, #ffffff 100%);
          border-bottom: 1px solid var(--hb-border-light);
        }

        .bulk-header h2 {
          margin: 0;
          font-size: 1.5rem;
          font-weight: 600;
          color: var(--hb-slate);
        }

        .toggle {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .toggle:hover {
          background: var(--hb-sea-dark);
          transform: translateY(-1px);
        }

        .bulk-body {
          display: flex;
          flex-direction: column;
          gap: 1rem;
          padding: 1rem;
        }

        .row {
          display: flex;
          gap: 0.75rem;
          align-items: center;
        }

        .row input {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          font-size: 0.85rem;
          transition: border-color 0.2s;
          height: 28px;
        }

        .row input.compact {
          padding: 0.25rem 0.5rem;
          font-size: 0.8rem;
          height: 24px;
        }

        .row input:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 2px rgba(14, 165, 180, 0.1);
        }

        .rows {
          display: flex;
          flex-direction: column;
          gap: 0.25rem;
          margin-bottom: 1rem;
          border: 1px solid var(--hb-border-light);
          border-radius: var(--hb-radius);
          overflow: hidden;
        }

        .rows .row {
          align-items: stretch;
          padding: 0.25rem 0.5rem;
          min-height: 32px;
        }

        .rows .row.compact {
          padding: 0.125rem 0.375rem;
          min-height: 28px;
        }

        .rows .row.head {
          font-weight: 600;
          color: var(--hb-slate);
          background: var(--hb-bg);
          border-bottom: 1px solid var(--hb-border);
          font-size: 0.85rem;
          padding: 0.375rem 0.5rem;
        }

        .rows .row .col {
          display: flex;
          align-items: center;
        }

        .rows .row .col.phone {
          min-width: 160px;
        }

        .rows .row .col.phone.compact {
          min-width: 140px;
        }

        .rows .row .col.prop {
          min-width: 140px;
        }

        .rows .row .col.prop.compact {
          min-width: 120px;
        }

        .rows .row .col.ticks {
          gap: 0.25rem;
          flex-wrap: nowrap;
          min-width: 200px;
        }

        .rows .row .col.ticks.compact {
          gap: 0.125rem;
          min-width: 180px;
          flex-direction: row;
          justify-content: flex-start;
        }

        .rows .row .col.act {
          min-width: 40px;
          justify-content: flex-end;
        }

        .add .add-btn {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.25rem 0.5rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          font-size: 0.85rem;
          transition: background-color 0.2s;
          height: 24px;
        }

        .add .add-btn:hover {
          background: var(--hb-sea-dark);
        }

        .del {
          background: #dc3545;
          color: white;
          border: none;
          padding: 0.4rem 0.6rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-size: 0.9rem;
          transition: background-color 0.2s;
        }

        .del:hover {
          background: #c82333;
        }

        .section {
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          padding: 0.75rem;
          background: var(--hb-bg);
        }

        .section h3 {
          margin: 0 0 0.75rem 0;
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--hb-slate);
        }

        .baseline .ticks {
          display: flex;
          gap: 1.5rem;
          flex-wrap: wrap;
        }

        .baseline .title {
          display: inline-block;
          min-width: 70px;
          color: var(--hb-muted);
          font-size: 0.9rem;
          margin-right: 0.5rem;
          font-weight: 500;
        }

        .baseline.compact {
          margin-top: 1rem;
        }

        .baseline.compact h4 {
          margin: 0 0 0.5rem 0;
          font-size: 1rem;
        }

        .baseline.compact .ticks {
          gap: 0.5rem;
        }

        .baseline-row.compact {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          flex-wrap: wrap;
        }

        .baseline-row.compact .title {
          min-width: 50px;
          font-size: 0.85rem;
          margin-right: 0.25rem;
        }

        .mini {
          display: flex;
          align-items: center;
          gap: 0.125rem;
          font-size: 0.8rem;
          color: var(--hb-slate);
          white-space: nowrap;
          flex-shrink: 0;
        }

        .mini input[type="checkbox"] {
          width: 14px;
          height: 14px;
          margin: 0;
        }

        /* Ensure mini labels match header spacer widths */
        .col.ticks.compact .mini:nth-child(1) { min-width: 20px; } /* All */
        .col.ticks.compact .mini:nth-child(2) { min-width: 28px; } /* Some */
        .col.ticks.compact .mini:nth-child(3) { min-width: 22px; } /* Req */
        .col.ticks.compact .mini:nth-child(4) { min-width: 24px; } /* Near */

        /* Alcohol mini labels */
        .col.ticks.compact:nth-child(5) .mini:nth-child(1) { min-width: 24px; } /* Prop */
        .col.ticks.compact:nth-child(5) .mini:nth-child(2) { min-width: 28px; } /* Rest */
        .col.ticks.compact:nth-child(5) .mini:nth-child(3) { min-width: 28px; } /* Room */

        /* Leisure mini labels */
        .col.ticks.compact:nth-child(6) .mini:nth-child(1) { min-width: 22px; } /* Mod */
        .col.ticks.compact:nth-child(6) .mini:nth-child(2) { min-width: 22px; } /* Wmn */
        .col.ticks.compact:nth-child(6) .mini:nth-child(3) { min-width: 24px; } /* Priv */

        .del.compact {
          padding: 0.125rem 0.25rem;
          font-size: 0.8rem;
          min-width: 24px;
          height: 24px;
        }

        .bulk-pagination {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin: 1rem 0;
          padding: 0.75rem;
          background: var(--hb-bg-secondary);
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          box-shadow: var(--hb-shadow);
        }

        .bulk-pagination .pagination-btn {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.375rem 0.75rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          font-size: 0.85rem;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 0.25rem;
        }

        .bulk-pagination .pagination-btn:hover:not(:disabled) {
          background: var(--hb-sea-dark);
          transform: translateY(-1px);
        }

        .bulk-pagination .pagination-btn:disabled {
          background: var(--hb-muted);
          cursor: not-allowed;
          transform: none;
          opacity: 0.6;
        }

        .bulk-pagination .pagination-info {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.125rem;
        }

        .bulk-pagination .page-indicator {
          font-weight: 600;
          color: var(--hb-slate);
          font-size: 0.85rem;
        }

        .bulk-pagination .item-count {
          color: var(--hb-muted);
          font-size: 0.75rem;
        }

        .col.ticks.compact.header {
          flex-direction: column;
          align-items: flex-start;
          padding: 0.125rem 0.375rem;
        }

        .header-label {
          font-size: 0.85rem;
          font-weight: 600;
          color: var(--hb-slate);
          margin-bottom: 0.125rem;
        }

        .header-spacers {
          display: flex;
          gap: 0.125rem;
          align-items: center;
          width: 100%;
        }

        .spacer {
          font-size: 0.75rem;
          color: transparent;
          white-space: nowrap;
          flex-shrink: 0;
          text-align: center;
        }

        /* Halal column spacers */
        .halal-header .header-spacers .spacer:nth-child(1) { min-width: 20px; } /* All */
        .halal-header .header-spacers .spacer:nth-child(2) { min-width: 28px; } /* Some */
        .halal-header .header-spacers .spacer:nth-child(3) { min-width: 22px; } /* Req */
        .halal-header .header-spacers .spacer:nth-child(4) { min-width: 24px; } /* Near */

        /* Alcohol column spacers */
        .alcohol-header .header-spacers .spacer:nth-child(1) { min-width: 24px; } /* Prop */
        .alcohol-header .header-spacers .spacer:nth-child(2) { min-width: 28px; } /* Rest */
        .alcohol-header .header-spacers .spacer:nth-child(3) { min-width: 28px; } /* Room */

        /* Leisure column spacers */
        .leisure-header .header-spacers .spacer:nth-child(1) { min-width: 22px; } /* Mod */
        .leisure-header .header-spacers .spacer:nth-child(2) { min-width: 22px; } /* Wmn */
        .leisure-header .header-spacers .spacer:nth-child(3) { min-width: 24px; } /* Priv */

        .mapping {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 0.75rem;
        }

        .map-row {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .map-row label {
          min-width: 170px;
          font-size: 0.9rem;
          color: var(--hb-slate);
          font-weight: 500;
        }

        .map-row select {
          flex: 1;
          padding: 0.5rem 0.75rem;
          border: 2px solid var(--hb-border);
          border-radius: var(--hb-radius);
          background: white;
          font-size: 0.9rem;
          transition: border-color 0.2s;
        }

        .map-row select:focus {
          outline: none;
          border-color: var(--hb-sea);
          box-shadow: 0 0 0 3px rgba(14, 165, 180, 0.1);
        }

        .csv-info {
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #bae6fd;
          padding: 0.75rem 1rem;
          border-radius: var(--hb-radius);
          margin-bottom: 0.75rem;
        }

        .csv-info p {
          margin: 0 0 0.5rem 0;
          color: var(--hb-slate);
          font-size: 0.9rem;
        }

        .csv-info ul {
          margin: 0 0 0.75rem 1.5rem;
          color: var(--hb-muted);
          font-size: 0.9rem;
        }

        .csv-info code {
          background: white;
          padding: 0.125rem 0.375rem;
          border: 1px solid var(--hb-border);
          border-radius: 4px;
          font-size: 0.85rem;
        }

        .example-btn {
          background: var(--hb-sea);
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .example-btn:hover {
          background: var(--hb-sea-dark);
        }

        .import-actions {
          margin-top: 0.75rem;
        }

        .import {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.5rem 1rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 500;
          transition: background-color 0.2s;
        }

        .import:hover {
          background: #059669;
        }

        .actions {
          display: flex;
          align-items: center;
          gap: 1rem;
          padding-top: 1rem;
          border-top: 1px solid var(--hb-border-light);
        }

        .start {
          background: #10b981;
          color: white;
          border: none;
          padding: 0.75rem 1.5rem;
          border-radius: var(--hb-radius);
          cursor: pointer;
          font-weight: 600;
          font-size: 1rem;
          transition: all 0.2s;
          box-shadow: var(--hb-shadow);
        }

        .start:hover:not(:disabled) {
          background: #059669;
          transform: translateY(-1px);
        }

        .start:disabled {
          background: var(--hb-muted);
          cursor: not-allowed;
          transform: none;
          box-shadow: none;
        }

        .message {
          color: var(--hb-slate);
          font-size: 0.9rem;
          padding: 0.5rem 0.75rem;
          background: #ecfdf5;
          border: 1px solid #a7f3d0;
          border-radius: var(--hb-radius);
          font-weight: 500;
        }

        /* AI Upload Styles */
        .ai-status {
          margin-top: 0.75rem;
          padding: 0.75rem;
          background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
          border: 1px solid #bae6fd;
          border-radius: var(--hb-radius);
        }

        .ai-status .loading {
          color: var(--hb-sea);
          font-size: 0.9rem;
          font-weight: 500;
        }

        .ai-results {
          margin-top: 1.5rem;
          padding: 1.25rem;
          background: var(--hb-bg);
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
        }

        .ai-results h4 {
          margin: 0 0 0.75rem 0;
          color: var(--hb-slate);
          font-size: 1.1rem;
          font-weight: 600;
        }

        .analysis-summary {
          background: var(--hb-bg-secondary);
          padding: 0.75rem;
          border-radius: var(--hb-radius);
          margin-bottom: 1.25rem;
          font-size: 0.9rem;
          color: var(--hb-slate);
          border: 1px solid var(--hb-border);
        }

        .ai-mapping {
          display: flex;
          flex-direction: column;
          gap: 1.25rem;
        }

        .ai-mapping h5 {
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          color: var(--hb-slate);
          font-weight: 600;
        }

        .detected-columns, .halal-columns, .alcohol-columns, .leisure-columns {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 0.75rem;
        }

        .sample-preview {
          margin-top: 1.25rem;
        }

        .sample-preview h5 {
          margin: 0 0 0.75rem 0;
          font-size: 1rem;
          color: var(--hb-slate);
        }

        .sample-table {
          border: 1px solid var(--hb-border);
          border-radius: var(--hb-radius);
          overflow: hidden;
          box-shadow: var(--hb-shadow);
        }

        .sample-header {
          display: flex;
          background: var(--hb-bg);
          font-weight: 600;
          color: var(--hb-slate);
          border-bottom: 1px solid var(--hb-border);
        }

        .sample-row {
          display: flex;
          border-bottom: 1px solid var(--hb-border-light);
        }

        .sample-row:last-child {
          border-bottom: none;
        }

        .sample-cell {
          padding: 0.5rem 0.75rem;
          border-right: 1px solid var(--hb-border-light);
          flex: 1;
          font-size: 0.85rem;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          min-width: 0;
        }

        .sample-cell:last-child {
          border-right: none;
        }

        .sample-cell.header {
          background: var(--hb-bg);
          font-weight: 600;
        }
      `}</style>
    </div>
  );
};

export default BulkRunPanel;


